import React from "react";
import { useState, useRef } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import { HandleGridBody } from "components/common/HandleGridBody.js";
import AddNewContact from "./AddViewContact.jsx";
import SearchTextBox from "components/SearchComponent.jsx";
import { RedirectCell, GRID_PAGE_SIZES } from "components/common/GridComponents.jsx";
import { useSelector } from "react-redux";
import { useAccessRight, CaseAccessRights } from "components/UserAccessRights.ts";
import { RxCross2 } from "react-icons/rx";
import { Tooltip } from "@progress/kendo-react-tooltip";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { editContact } from "api";
import { toast } from "react-toastify";

const Contacts = (props) => {
  const [list, setLists] = useState({
    data: [],
    total: 20,
  });

  const [formView, setFormView] = useState(false);
  const [showAddNewForm, setshowAddNewForm] = useState(false);
  const [type, setType] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deletingContact, setDeletingContact] = useState(false);

  const [dataState, setDataState] = useState({
    take: 50,
    skip: 0,
    sort: [
      {
        field: "name",
        dir: "asc",
      },
    ],
  });

  const [isSearched, setIsSearched] = useState(false);
  const [eventDataState, setEventDataState] = useState();
  const [searchValue, setSearchValue] = useState();
  const [contactId, setContactaid] = useState(null);
  const searchRef = useRef();

  const canModifyContact = useAccessRight(CaseAccessRights.ModifyContact);

  const isLoadingContacts =
    useSelector((state) => state.contactsList.isLoading);

  const handleContactData = (dataItem) => {
    setFormView(true);
    setContactaid(dataItem.contactId);
  };

  const NameCell = (e) => {
    return (
      <td className="gridBoldField">
        <u
          style={{ cursor: "pointer", color: "black" }}
          onClick={() => {
            handleContactData(e.dataItem);
          }}
        >
          {e.dataItem.name}
        </u>
      </td>
    );
  };

  const ContactCommandCell = (props) => {
    return (
      <td>
        {props?.dataItem?.website ? (
          <a target="_blank" href={`https://${props.dataItem.website}`}>
            {props.dataItem.website}
          </a>
        ) : (
          <span>N/A</span>
        )}
      </td>
    );
  };

  const Redirect = (props) => (
    <RedirectCell handleClick={() => handleContactData(props.dataItem)} />
  );

  const handleDeleteClick = (dataItem) => {
    if (!dataItem?.contactId) return;
    setContactToDelete(dataItem);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete?.contactId) {
      setShowDeleteConfirm(false);
      setContactToDelete(null);
      return;
    }
    setDeletingContact(true);
    try {
      await editContact(contactToDelete.contactId, { active: false });
      toast.success("Contact removed");
      handleRefresh();
    } catch (err) {
      console.error(err);
    } finally {
      setShowDeleteConfirm(false);
      setContactToDelete(null);
      setDeletingContact(false);
    }
  };

  const DeleteContactCell = (props) => {
    if (!props.dataItem?.contactId) return <td />;
    return (
      <td className="text-right">
        <Tooltip anchorElement="target" position="top" parentTitle>
          <Button
            className="btn m-0 p-0 border-0"
            title="Remove contact"
            disabled={!canModifyContact}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(props.dataItem);
            }}
          >
            <RxCross2 size={15} color={canModifyContact ? "red" : "#ccc"} aria-hidden />
          </Button>
        </Tooltip>
      </td>
    );
  };

  const createState = (skip, take) => {
    let pagerSettings = {
      pageSizes: GRID_PAGE_SIZES,
    };
    return {
      pageSize: take,
      pageable: pagerSettings,
    };
  };
  const [state, setState] = React.useState(createState(0, 50));

  const dataStateChange = (e) => {
    const eventObject = { event: e?.dataState };
    setEventDataState(eventObject);
    if (
      e.dataState?.sort !== undefined &&
      e.dataState?.skip !== 0 &&
      dataState?.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState?.sort?.[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState?.sort?.[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  const dataReceived = (list) => {
    if (list.data) {
      setLists(list);
    }
  };

  const handleRefresh = () => {
    if (eventDataState) {
      setDataState({ ...eventDataState?.event, skip: 0 });
    } else {
      setDataState({
        ...dataState,
        take: 50,
        skip: 0,
      });
    }
  };

  return (
    <div className="page h-100 w-100 d-flex flex-column">
      <div className="d-flex align-items-center">
        <SearchTextBox
          ref={searchRef}
          changeFilterValue={(value) => {
            // New searches should always start from page 1
            setDataState((prev) => ({ ...prev, skip: 0 }));
            setSearchValue(value);
          }}
        />
        <Button
          className="ms-3 border-0 common-btn me-1"
          svgIcon={refreshIcon}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
        <Button
          className="common-btn ms-auto"
          disabled={!canModifyContact}
          onClick={() => setshowAddNewForm(true)}
        >
          Add Contact
        </Button>
      </div>

      <Grid
        {...dataState}
        data={list}
        onDataStateChange={dataStateChange}
        sortable={true}
        className="fs-md mt-3 flex-grow-1 overflow-auto"
        pageable={state.pageable}
        pageSize={state.pageSize}
        resizable={true}
      >
        <GridNoRecords>
          {isLoadingContacts ? (
            <span></span>
          ) : (
            list.data && list.data.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn field="name" title="Name" cell={NameCell} />
        <GridColumn field="type" title="Type" width="100px" />
        <GridColumn field="contactCategory" title="Category" width="120px" />
        <GridColumn field="phone" title="Phone" width="150px" />
        <GridColumn field="email" title="Email" width={"220px"} />
        <GridColumn field="fullAddress" title="Address" />
        <GridColumn width="30" cell={Redirect} />
        <GridColumn width="45" cell={DeleteContactCell} headerClassName="text-center" />
      </Grid>

      <HandleGridBody
        dataState={dataState}
        onDataReceived={dataReceived}
        currentComponent="contacts"
        searchValue={searchValue}
        isSearched={isSearched}
        showLoader={isLoadingContacts}
        active={true}
      />

      {(showAddNewForm || formView) && (
        <AddNewContact
          type={type}
          viewMode={formView}
          contactId={contactId}
          canModify={canModifyContact}
          close={(shouldRefresh = false) => {
            if (shouldRefresh) {
              handleRefresh();
            }
            setshowAddNewForm(false);
            setFormView(false);
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content="Are you sure you want to remove this contact?"
          onClickYes={confirmDeleteContact}
          onClickNo={() => {
            setShowDeleteConfirm(false);
            setContactToDelete(null);
          }}
          onClose={() => {
            setShowDeleteConfirm(false);
            setContactToDelete(null);
          }}
          isLoading={deletingContact}
        />
      )}
    </div>
  );
};
export default Contacts;
